import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Descriptions, Drawer, Empty, Form, Grid, Input, List, Result, Select, Space, Steps, Tag, message } from "antd";
import { EyeOutlined, FilePdfOutlined } from "@ant-design/icons";
import debounce from "lodash/debounce";
import dayjs from "dayjs";
import DatosActaFields, { aDatosActa } from "./DatosActaFields";
import {
  buscarParticipantes,
  crearManifestacion,
  getProyectos,
  mensajeDeError,
} from "../../../services/manifestacionesInteres/manifestacionesInteresService";

const TIPO_CEDULA = 1;
const PASOS = ["Proyecto", "Participante", "Datos del acta", "Revisar", "Generado"];
const ANCHO_DRAWER = 820;

export default function GenerarManifestacionDrawer({ open, onClose, onGenerada, onVerDocumento, onVerExistente }) {
  const pantallas = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [paso, setPaso] = useState(0);
  const [proyectos, setProyectos] = useState([]);
  const [proyecto, setProyecto] = useState(null);
  const [participante, setParticipante] = useState(null);
  const [resultados, setResultados] = useState({ total: 0, datos: [] });
  const [buscando, setBuscando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [paginaBusqueda, setPaginaBusqueda] = useState(1);
  const [datosActa, setDatosActa] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);
  const [generada, setGenerada] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPaso(0);
    setProyecto(null);
    setParticipante(null);
    setResultados({ total: 0, datos: [] });
    setBusqueda("");
    setPaginaBusqueda(1);
    setDatosActa(null);
    setError(null);
    setGenerada(null);
    form.resetFields();
    getProyectos()
      .then(setProyectos)
      .catch(async (e) => message.error(await mensajeDeError(e, "No se pudieron cargar los proyectos.")));
  }, [open, form]);

  const consultar = useMemo(
    () =>
      debounce(async (oidProyecto, texto, pagina) => {
        setBuscando(true);
        try {
          setResultados(await buscarParticipantes(oidProyecto, texto, pagina));
          setPaginaBusqueda(pagina);
        } catch (e) {
          message.error(await mensajeDeError(e, "No se pudieron buscar los participantes."));
        } finally {
          setBuscando(false);
        }
      }, 300),
    []
  );

  useEffect(() => () => consultar.cancel(), [consultar]);

  const irAParticipantes = () => {
    setPaso(1);
    consultar(proyecto.oid, busqueda, 1);
  };

  const elegirParticipante = (p) => {
    setParticipante(p);
    form.setFieldsValue({ telefono_acta: p.telefono || "", fecha_manifestacion: dayjs() });
    setPaso(2);
  };

  const irARevisar = async () => {
    const valores = await form.validateFields();
    setDatosActa(valores);
    setError(null);
    setPaso(3);
  };

  const generar = async () => {
    setGenerando(true);
    setError(null);
    try {
      const detalle = await crearManifestacion({
        oid_proyecto: proyecto.oid,
        oid_participante: participante.oid,
        ...aDatosActa(datosActa),
      });
      setGenerada(detalle);
      setPaso(4);
      message.success("Manifestación generada.");
      onGenerada();
    } catch (e) {
      setError(await mensajeDeError(e, "No se pudo generar la manifestación."));
    } finally {
      setGenerando(false);
    }
  };

  const resumen = participante && (
    <Descriptions bordered size="small" column={1} className="mb-3" title="Datos del sistema">
      <Descriptions.Item label="Proyecto">{proyecto?.codigo} - {proyecto?.proyecto}</Descriptions.Item>
      <Descriptions.Item label="Participante">{participante.nombre_participante}</Descriptions.Item>
      <Descriptions.Item label="Documento">{participante.tipo_identificacion} {participante.numero_identificacion}</Descriptions.Item>
    </Descriptions>
  );

  return (
    <Drawer
      title="Generar manifestación de interés"
      open={open}
      onClose={onClose}
      width={pantallas.md ? ANCHO_DRAWER : "100%"}
      destroyOnHidden
      footer={
        paso < 4 && (
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            {paso > 0 && <Button disabled={generando} onClick={() => setPaso(paso - 1)}>Atrás</Button>}
            {paso === 0 && <Button type="primary" disabled={!proyecto} onClick={irAParticipantes}>Continuar</Button>}
            {paso === 2 && <Button type="primary" onClick={irARevisar}>Revisar</Button>}
            {paso === 3 && (
              <Button type="primary" icon={<FilePdfOutlined />} loading={generando} onClick={generar}>
                Generar PDF
              </Button>
            )}
          </Space>
        )
      }
    >
      {pantallas.md ? (
        <Steps current={paso} size="small" items={PASOS.map((title) => ({ title }))} className="mb-4" />
      ) : (
        <p className="mb-3"><b>Paso {paso + 1} de {PASOS.length}:</b> {PASOS[paso]}</p>
      )}

      {paso === 0 && (
        <Form layout="vertical">
          <Form.Item label="Proyecto" required>
            <Select
              showSearch
              placeholder="Busque por código o nombre"
              optionFilterProp="label"
              value={proyecto?.oid}
              onChange={(oid) => setProyecto(proyectos.find((p) => p.oid === oid))}
              options={proyectos.map((p) => ({ value: p.oid, label: `${p.codigo} - ${p.proyecto}` }))}
            />
          </Form.Item>
        </Form>
      )}

      {paso === 1 && (
        <>
          <Input.Search
            allowClear
            placeholder="Buscar por cédula, nombre o apellidos"
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              consultar(proyecto.oid, e.target.value, 1);
            }}
            className="mb-3"
          />
          <List
            loading={buscando}
            dataSource={resultados.datos}
            rowKey="oid"
            locale={{ emptyText: <Empty description="No hay participantes con esa búsqueda en este proyecto" /> }}
            pagination={{
              current: paginaBusqueda,
              total: resultados.total,
              pageSize: 10,
              showSizeChanger: false,
              size: "small",
              onChange: (pagina) => consultar(proyecto.oid, busqueda, pagina),
            }}
            renderItem={(p) => {
              const sinCedula = p.oid_tipo_identificacion !== TIPO_CEDULA;
              return (
                <List.Item
                  actions={[
                    p.oid_manifestacion ? (
                      <Button key="ver" size="small" onClick={() => onVerExistente(p.oid_manifestacion)}>Ver registro</Button>
                    ) : (
                      <Button key="elegir" size="small" type="primary" disabled={sinCedula} onClick={() => elegirParticipante(p)}>
                        Seleccionar
                      </Button>
                    ),
                  ]}
                >
                  <List.Item.Meta
                    title={<>{p.nombre_participante} {p.oid_manifestacion && <Tag color="blue">Ya tiene manifestación</Tag>}</>}
                    description={
                      <>
                        {p.tipo_identificacion} {p.numero_identificacion}
                        {sinCedula && (
                          <div className="text-danger">
                            El acta oficial solo contempla cédula de ciudadanía. Debe definirse el texto con el área jurídica.
                          </div>
                        )}
                      </>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </>
      )}

      {/* El formulario se conserva al pasar a Revisar para poder volver sin perder datos. */}
      <div style={{ display: paso === 2 ? "block" : "none" }}>
        {resumen}
        <Form form={form} layout="vertical">
          <DatosActaFields />
        </Form>
      </div>

      {paso === 3 && datosActa && (
        <>
          {error && <Alert type="error" showIcon message={error} className="mb-3" />}
          {resumen}
          <Descriptions bordered size="small" column={1} title="Datos del acta">
            <Descriptions.Item label="Ciudad de expedición">{datosActa.ciudad_expedicion}</Descriptions.Item>
            <Descriptions.Item label="Fecha de manifestación">{datosActa.fecha_manifestacion.format("DD/MM/YYYY")}</Descriptions.Item>
            <Descriptions.Item label="Teléfono">{datosActa.telefono_acta || "Sin teléfono: queda la línea en blanco"}</Descriptions.Item>
            <Descriptions.Item label="Profesional responsable">{datosActa.nombre_profesional}</Descriptions.Item>
          </Descriptions>
        </>
      )}

      {paso === 4 && generada && (
        <Result
          status="success"
          title="Manifestación generada"
          subTitle={`Estado: pendiente de carga. Imprima el acta, recoja las firmas y la huella, y cárguela firmada. Archivo: ${generada.documentos[0].nombre_fisico}`}
          extra={[
            <Button key="ver" type="primary" icon={<EyeOutlined />} onClick={() => onVerDocumento(generada.oid, generada.documentos[0])}>
              Ver PDF
            </Button>,
            <Button key="cerrar" onClick={onClose}>Cerrar</Button>,
          ]}
        />
      )}
    </Drawer>
  );
}
